import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import client from "../../api/client";

const STATUS_COLORS = {
  active:   "bg-green-100 text-green-700",
  inactive: "bg-gray-100 text-gray-500",
  pending:  "bg-amber-100 text-amber-700",
  locked:   "bg-red-100 text-red-700",
};

export default function SubscriptionsPage() {
  const [subs, setSubs] = useState([]);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [exporting, setExporting] = useState(false);

  const load = (p = 1) => {
    const token = localStorage.getItem("nd-rest-tkn");
    if (token) client.defaults.headers.Authorization = `Bearer ${token}`;
    setLoading(true);
    client.get("/admin/subscriptions", { params: { search, status, page: p } })
      .then(res => { setSubs(res.data.data); setMeta(res.data); setPage(p); })
      .catch(() => toast.error("Failed to load subscriptions."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(1); }, [search, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await client.get("/admin/subscriptions/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "subscriptions.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed."); }
    finally { setExporting(false); }
  };

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Subscriptions</h1>
          <p className="text-sm text-gray-500 mt-0.5">{meta?.total ? `${meta.total} total` : "All subscriptions"}</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <select value={status} onChange={e => setStatus(e.target.value)} className="border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 bg-white">
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending">Pending</option>
          </select>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or email…"
            className="border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#294a93]/30 w-52" />
          <button onClick={exportCsv} disabled={exporting} className="bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors disabled:opacity-50">
            {exporting ? "Exporting…" : "⬇ Export CSV"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
        {loading ? (
          <div className="p-8 space-y-3">{[1,2,3,4,5].map(i => <div key={i} className="h-12 bg-gray-100 rounded animate-pulse" />)}</div>
        ) : subs.length === 0 ? (
          <div className="p-16 text-center"><p className="text-4xl mb-3">💳</p><p className="text-sm font-medium text-gray-600">No subscriptions found</p></div>
        ) : (
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>{["Student", "Email", "Course", "Cohort", "Status", "Amount", "Payment Date", "Cert Eligible"].map(h => (
                <th key={h} className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {subs.map(s => (
                <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 font-medium text-gray-800">{s.user?.name}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{s.user?.email}</td>
                  <td className="px-4 py-3 text-gray-600 text-xs max-w-[160px] truncate">{s.course || "—"}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{s.cohort || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${STATUS_COLORS[s.status] || "bg-gray-100 text-gray-500"}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-semibold text-gray-800">{s.amount ? `₦${Number(s.amount).toLocaleString()}` : "—"}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{s.payment_date ? new Date(s.payment_date).toLocaleDateString() : "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${s.certificate_eligible ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"}`}>
                      {s.certificate_eligible ? "Yes" : "No"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {meta && meta.last_page > 1 && (
        <div className="flex justify-center gap-2 mt-6">
          <button disabled={page === 1} onClick={() => load(page - 1)} className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium disabled:opacity-40 hover:bg-gray-50 transition-colors">← Prev</button>
          <span className="px-4 py-2 text-sm text-gray-500">Page {page} of {meta.last_page}</span>
          <button disabled={page === meta.last_page} onClick={() => load(page + 1)} className="px-4 py-2 rounded-xl border border-gray-200 text-sm font-medium disabled:opacity-40 hover:bg-gray-50 transition-colors">Next →</button>
        </div>
      )}
    </div>
  );
}
